import { useState, useEffect, useCallback, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Simple in-memory cache
const cache = new Map<string, CacheEntry<unknown>>();

export interface UseApiQueryOptions<T> {
  enabled?: boolean;
  cacheTime?: number; // in milliseconds
  refetchInterval?: number; // in milliseconds
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
  /**
   * Quando `true`, o cacheKey NÃO é particionado por establishment.
   * Use para endpoints globais (não dependentes do `x-establishment-id`):
   * lista de estabelecimentos, /auth/me, navegação global, etc.
   */
  globalScope?: boolean;
}

/** Lê o establishment corrente do localStorage para particionar a cache. */
function getCurrentEstablishmentScope(): string {
  if (typeof window === 'undefined') return 'global';
  try {
    return localStorage.getItem('mep_current_establishment') ?? 'global';
  } catch {
    return 'global';
  }
}

export function useApiQuery<T>(
  key: string | readonly unknown[],
  queryFn: () => Promise<T>,
  options: UseApiQueryOptions<T> = {}
) {
  const {
    enabled = true,
    cacheTime = 5 * 60 * 1000, // 5 minutes default
    refetchInterval,
    onSuccess,
    onError,
    globalScope = false,
  } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousKeyRef = useRef<string>('');
  const wasEnabledRef = useRef<boolean>(enabled);
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Improved serialization to handle objects in the key array
  const rawCacheKey = Array.isArray(key)
    ? key
        .map((k) => {
          if (typeof k === 'object' && k !== null) {
            // Sort keys for consistency
            const sorted = Object.keys(k)
              .sort()
              .reduce((obj, key) => {
                obj[key] = (k as Record<string, unknown>)[key];
                return obj;
              }, {} as Record<string, unknown>);
            return JSON.stringify(sorted);
          }
          return String(k);
        })
        .join(':')
    : String(key);

  // CRÍTICO: particionar a cache por establishment para evitar vazamento
  // de dados entre condomínios. Hooks de endpoints globais devem passar
  // `globalScope: true` no options.
  const cacheKey = globalScope
    ? rawCacheKey
    : `est:${getCurrentEstablishmentScope()}:${rawCacheKey}`;

  // Determine initial cache usage BEFORE state creation to avoid first-render flicker
  const isListQuery = cacheKey.includes('"page"') || cacheKey.includes('"limit"');
  const cachedEntry = !isListQuery ? cache.get(cacheKey) : undefined;
  const isCacheFresh = !!cachedEntry && Date.now() - (cachedEntry as CacheEntry<T>).timestamp < cacheTime;

  // Initialize from cache when available and fresh
  const [data, setData] = useState<T | undefined>(
    isCacheFresh ? (cachedEntry as CacheEntry<T>).data : undefined
  );
  const [error, setError] = useState<unknown>(null);
  // Start in loading when enabled and no fresh cache
  const [isLoading, setIsLoading] = useState<boolean>(enabled && !isCacheFresh);
  const [isFetching, setIsFetching] = useState(false);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    // Prevent concurrent fetches
    if (isFetching) return;

    // Check cache first - but skip for list queries with pagination
    const cached = cache.get(cacheKey) as CacheEntry<T> | undefined;
    if (!isListQuery && cached && Date.now() - cached.timestamp < cacheTime) {
      setData(cached.data);
      setIsLoading(false);
      return;
    }

    setIsFetching(true);
    // Only set isLoading if we don't have any data yet (initial load)
    if (data === undefined) setIsLoading(true);

    try {
      const result = await queryFnRef.current();
      setData(result);
      setError(null);

      // Update cache - result is already of type T, cast to unknown for storage
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      onSuccessRef.current?.(result);
    } catch (err) {
      setError(err);
      onErrorRef.current?.(err);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [cacheKey, enabled, cacheTime, data, isFetching, isListQuery]);

  // Initial fetch - only run if enabled and key changed
  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;
    const enabledJustActivated = !wasEnabled && enabled;

    if (enabled && (cacheKey !== previousKeyRef.current || enabledJustActivated)) {
      previousKeyRef.current = cacheKey;
      fetchData();
    }
  }, [fetchData, enabled, cacheKey]);

  // Set up refetch interval
  useEffect(() => {
    if (refetchInterval && enabled) {
      intervalRef.current = setInterval(fetchData, refetchInterval);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
    return undefined;
  }, [refetchInterval, enabled, fetchData]);

  // Global invalidation listener to refetch when prefix matches this query key.
  // Accepts both raw prefixes (e.g., 'lost-found') and est-scoped prefixes —
  // see invalidateQueries() for symmetric logic.
  useEffect(() => {
    const handler = (e: Event) => {
      const anyEvent = e as CustomEvent<{ prefix?: string }>;
      const prefix = anyEvent.detail?.prefix ?? '';
      if (!prefix) {
        fetchData();
        return;
      }
      if (cacheKey.startsWith(prefix)) {
        fetchData();
        return;
      }
      if (cacheKey.startsWith('est:')) {
        const afterEst = cacheKey.indexOf(':', 4);
        if (afterEst !== -1) {
          const remainder = cacheKey.slice(afterEst + 1);
          if (remainder.startsWith(prefix)) {
            fetchData();
          }
        }
      }
    };
    window.addEventListener('api-invalidate', handler as EventListener);
    return () => window.removeEventListener('api-invalidate', handler as EventListener);
  }, [cacheKey, fetchData]);

  // Listen for auth-logout event to clear ALL user-specific data (prevent stale data between users)
  // This is critical to avoid data from previous user being shown after logout/login
  useEffect(() => {
    const handler = () => {
      // Clear ALL queries during logout, including auth queries
      // This prevents establishments/permissions from previous user being used after new login
      setData(undefined);
      setError(null);
      setIsLoading(false);
      setIsFetching(false);
    };
    window.addEventListener('auth-logout', handler);
    return () => window.removeEventListener('auth-logout', handler);
  }, [cacheKey]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  return {
    data,
    error,
    isLoading,
    isFetching,
    refetch
  };
}

// Mutation hook
interface UseApiMutationOptions<TData, TVariables> {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: unknown, variables: TVariables) => void;
}

export function useApiMutation<TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: UseApiMutationOptions<TData, TVariables> = {}
) {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Use refs to always access the latest mutationFn and options,
  // preventing stale closures when the async mutation is in-flight across re-renders
  const mutationFnRef = useRef(mutationFn);
  mutationFnRef.current = mutationFn;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutate = useCallback(async (variables?: TVariables) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await mutationFnRef.current(variables as TVariables);
      setData(result);
      optionsRef.current.onSuccess?.(result, variables as TVariables);
      return result;
    } catch (err) {
      setError(err);
      optionsRef.current.onError?.(err, variables as TVariables);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const mutateAsync = mutate;

  return {
    mutate,
    mutateAsync,
    data,
    error,
    isLoading,
    isPending: isLoading
  };
}

// Invalidate cache entries
export function invalidateQueries(keyPrefix?: string | readonly string[]) {
  if (!keyPrefix) {
    cache.clear();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-invalidate', { detail: { prefix: '' } }));
    }
    return;
  }

  const prefix = typeof keyPrefix === 'string' ? keyPrefix : keyPrefix.join(':');

  // O cacheKey real é prefixado com `est:<id>:` para queries particionadas
  // por establishment (ver `useApiQuery`). Aceitamos tanto o prefixo
  // "cru" passado pelos consumidores (ex: 'lost-found') quanto o prefixo
  // já com `est:`, fazendo um includes() permissivo.
  const matches = (key: string): boolean => {
    if (key.startsWith(prefix)) return true;
    // Tentativa de match após o segmento "est:<id>:"
    if (key.startsWith('est:')) {
      const afterEst = key.indexOf(':', 4); // posição do ':' após "est:"
      if (afterEst !== -1) {
        const remainder = key.slice(afterEst + 1);
        return remainder.startsWith(prefix);
      }
    }
    return false;
  };

  for (const key of cache.keys()) {
    if (matches(key)) {
      cache.delete(key);
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('api-invalidate', { detail: { prefix } }));
  }
}

// Clear cache without triggering refetch - use during logout to prevent stale data
export function clearCache() {
  cache.clear();
  // Dispatch auth-logout event so contexts can clear their state
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-logout'));
  }
}

// Listen for global invalidation events in all queries and refetch when matching
// This enables "real-time" updates after create/update/delete without manual refetch
if (typeof window !== 'undefined') {
  // Cross-tab: quando outra aba troca o establishment, limpamos a cache
  // local para que esta aba não exponha dados do estabelecimento anterior.
  // (No mesmo tab, o EstablishmentContext faz reload — sem necessidade aqui.)
  window.addEventListener('storage', (event) => {
    if (
      event.key === 'mep_current_establishment' &&
      event.newValue !== event.oldValue
    ) {
      cache.clear();
      window.dispatchEvent(
        new CustomEvent('api-invalidate', { detail: { prefix: '' } }),
      );
    }
  });
}
